import {ensureAbilityCastTextures} from '../utils/effects/abilityCastTextures';
import {ensureCellOutlineTextures} from '../utils/effects/cellOutlineTextures';
import {ensureHexWaveTextures} from '../utils/effects/hexWaveTextures';
import {ensureMeleeTextures} from '../utils/effects/meleeEffectTextures';
import {ensureSizeRecruitmentTextures} from '../utils/effects/sizeRecruitmentTextures';
import {shakeFieldCamera} from '../utils/effects/shakeFieldCamera';

/**
 * АНИМАЦИИ СПОСОБНОСТЕЙ ФРАКЦИЙ на поле: рисует план из
 * utils/effects/buildAbilityCast (что и когда показывать — решает
 * scenes/playAbilityCastEffects).
 *
 * Спрайты анимации принадлежат ей, а не ячейкам: поле перерисовывается на
 * каждое обновление стора, а анимация идет поверх и переживает перерисовки.
 *
 * Время идет по кадрам сцены (твин), а не по настенным часам: в скрытой
 * вкладке анимация стоит. Темп чужих действий сжимает ее целиком (timeScale).
 *
 * Анимация — украшение: ее сбой не должен ронять кадр, поэтому ошибка
 * обрывает только эту анимацию (итог при этом все равно открывается).
 */
export default class AbilityCastAnimation {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} options
   * @param {{air: number, ground: number, wings: number}} options.depths — слои:
   *   над полем, на земле и за иконкой летящего отряда
   * @param {number} [options.linearFilter] — режим фильтрации LINEAR для текстур
   * @param {number} [options.addBlendMode] — режим сложения цвета (Phaser.BlendModes.ADD)
   */
  constructor(scene, {depths, linearFilter, addBlendMode} = {}) {
    this.scene = scene;
    this.depths = depths;
    this.linearFilter = linearFilter;
    this.addBlendMode = addBlendMode;
    this.animations = new Map();
    this.nextId = 1;
  }

  /**
   * Запустить анимацию по плану
   *
   * @param {Object} plan — см. buildAbilityCast
   * @param {Object} [options]
   * @param {number} [options.timeScale] — во сколько раз быстрее обычного
   *   идет время анимации (1 — обычная скорость; 0.5 — вдвое короче)
   * @param {function(number): void} [options.onFrame] — каждый кадр анимации, с
   *   ее временем: так за планом идут спрайты, которые рисует не он (иконка
   *   летящего отряда)
   * @param {function(): void} [options.onImpact] — момент попадания
   * @param {function(): void} [options.onComplete] — анимация ушла с поля
   *   (доиграла или оборвана)
   * @returns {number|null} id анимации; null — показать не вышло
   */
  play(plan, {timeScale = 1, onFrame, onImpact, onComplete} = {}) {
    if (!plan || !this.scene?.add || !this.scene.tweens) {
      return null;
    }
    const isTexturesReady = ensureAbilityCastTextures(this.scene, {linearFilter: this.linearFilter})
      && ensureMeleeTextures(this.scene, {linearFilter: this.linearFilter})
      && ensureSizeRecruitmentTextures(this.scene, {linearFilter: this.linearFilter})
      && ensureCellOutlineTextures(this.scene, {linearFilter: this.linearFilter})
      && ensureHexWaveTextures(this.scene, {linearFilter: this.linearFilter});
    if (!isTexturesReady) {
      return null;
    }

    const id = this.nextId;
    this.nextId += 1;
    const animation = {
      id,
      plan,
      clock: {elapsedMs: 0},
      isImpacted: false,
      onFrame,
      onImpact,
      onComplete,
      tween: null,
      sprites: plan.elements.map(() => null),
      graphics: (plan.outlines || []).map(() => null),
    };
    this.animations.set(id, animation);

    try {
      this.render(animation);
      animation.tween = this.scene.tweens.add({
        targets: animation.clock,
        elapsedMs: plan.totalMs,
        duration: Math.max(1, Math.round(plan.totalMs * timeScale)),
        ease: 'Linear',
        onUpdate: () => this.guard(animation, () => this.render(animation)),
        onComplete: () => this.finish(id),
      });
    } catch (error) {
      console.warn('[AbilityCast] не удалось запустить анимацию', error);
      this.finish(id);
      return null;
    }
    return id;
  }

  /** Идет ли анимация с таким id */
  checkIfPlaying(id) {
    return this.animations.has(id);
  }

  /**
   * Довести анимацию до конца сразу: итог открывается, спрайты убираются
   * (подстраховка удержания итога, разбор поля)
   */
  finish(id) {
    const animation = this.animations.get(id);
    if (!animation) {
      return;
    }
    this.animations.delete(id);
    try {
      animation.tween?.stop?.();
      this.scene.tweens?.killTweensOf?.(animation.clock);
    } catch (error) {
      console.warn('[AbilityCast] не удалось остановить твин анимации', error);
    }
    this.fireImpact(animation);
    animation.sprites.forEach((sprite) => sprite?.destroy?.());
    animation.graphics.forEach((graphics) => graphics?.destroy?.());
    animation.onComplete?.();
  }

  /** Убрать все анимации (разбор поля) */
  clear() {
    [...this.animations.keys()].forEach((id) => this.finish(id));
  }

  guard(animation, action) {
    try {
      action();
    } catch (error) {
      console.warn('[AbilityCast] анимация оборвана из-за ошибки', error);
      this.finish(animation.id);
    }
  }

  fireImpact(animation) {
    if (animation.isImpacted) {
      return;
    }
    animation.isImpacted = true;
    const {shake} = animation.plan;
    if (shake && this.animations.has(animation.id)) {
      shakeFieldCamera(this.scene, shake);
    }
    try {
      animation.onImpact?.();
    } catch (error) {
      console.warn('[AbilityCast] ошибка в обработчике попадания', error);
    }
  }

  render(animation) {
    const {plan, clock} = animation;
    const elapsedMs = clock.elapsedMs;

    plan.elements.forEach((element, index) => {
      const isVisible = elapsedMs >= element.startMs && elapsedMs < element.endMs;
      let sprite = animation.sprites[index];
      if (!isVisible) {
        // Окно прошло — спрайт больше не нужен
        if (sprite && elapsedMs >= element.endMs) {
          sprite.destroy();
          animation.sprites[index] = null;
        }
        return;
      }
      if (!sprite) {
        sprite = this.createSprite(element);
        animation.sprites[index] = sprite;
      }
      const local = (elapsedMs - element.startMs) / (element.endMs - element.startMs);
      this.applyPose(sprite, element, element.pose(local, elapsedMs));
    });

    (plan.outlines || []).forEach((outline, index) => {
      const isVisible = elapsedMs >= outline.startMs && elapsedMs < outline.endMs;
      let graphics = animation.graphics[index];
      if (!isVisible) {
        if (graphics && elapsedMs >= outline.endMs) {
          graphics.destroy();
          animation.graphics[index] = null;
        }
        return;
      }
      if (!graphics) {
        graphics = this.createOutline(outline);
        animation.graphics[index] = graphics;
      }
      const local = (elapsedMs - outline.startMs) / (outline.endMs - outline.startMs);
      const pose = outline.pose(local, elapsedMs);
      graphics.setPosition(pose.x, pose.y);
      graphics.setScale(pose.scale ?? 1);
      graphics.setAlpha(Math.min(1, Math.max(0, pose.alpha ?? 1)));
    });

    animation.onFrame?.(elapsedMs);

    if (!animation.isImpacted && elapsedMs >= plan.impactMs) {
      this.fireImpact(animation);
    }
  }

  createSprite(element) {
    const sprite = this.scene.add.sprite(0, 0, element.textureKey, element.frame);
    sprite.setOrigin(element.origin?.x ?? 0.5, element.origin?.y ?? 0.5);
    sprite.setDepth((this.depths[element.layer] ?? this.depths.air) + element.order * 0.0001);
    if (element.blend === 'add' && this.addBlendMode !== undefined) {
      sprite.setBlendMode(this.addBlendMode);
    }
    sprite.setTint(element.tint);
    sprite.baseScale = element.sizePx / (sprite.width || 1);
    sprite.currentTint = element.tint;
    return sprite;
  }

  applyPose(sprite, element, pose) {
    const scaleX = (pose.scaleX ?? pose.scale ?? 1) * sprite.baseScale;
    const scaleY = (pose.scaleY ?? pose.scale ?? 1) * sprite.baseScale;
    sprite.setPosition(pose.x, pose.y);
    sprite.setScale(scaleX, scaleY);
    sprite.setAlpha(Math.min(1, Math.max(0, pose.alpha ?? 1)));
    sprite.setRotation(pose.rotation ?? 0);
    if (pose.layer !== undefined && pose.layer !== sprite.currentLayer) {
      sprite.setDepth(this.depths[pose.layer] + element.order * 0.0001);
      sprite.currentLayer = pose.layer;
    }
    if (pose.tint !== undefined && pose.tint !== sprite.currentTint) {
      sprite.setTint(pose.tint);
      sprite.currentTint = pose.tint;
    }
  }

  // Контур ячейки рисуется вокруг своего центра: так его можно сжимать и
  // раздувать масштабом
  createOutline(outline) {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(this.depths.air);
    graphics.lineStyle(outline.widthPx, outline.color, 1);
    graphics.strokePoints(outline.points, true, true);
    return graphics;
  }
}
